import React, { useState, useEffect, useRef, useCallback } from 'react';

const ICE_SERVERS = { iceServers: [{ urls: 'stun:stun.l.google.com:19302' }] };

export default function VoiceChat({ socket, players, mySeat }) {
  const [enabled, setEnabled] = useState(false);
  const [muted, setMuted] = useState(false);
  const [status, setStatus] = useState('off'); // off, connecting, connected, error
  const localStreamRef = useRef(null);
  const peersRef = useRef({}); // socketId → RTCPeerConnection
  const audioElementsRef = useRef({});

  const cleanup = useCallback(() => {
    Object.values(peersRef.current).forEach(pc => pc.close());
    peersRef.current = {};
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(t => t.stop());
      localStreamRef.current = null;
    }
    Object.values(audioElementsRef.current).forEach(el => { el.srcObject = null; });
    audioElementsRef.current = {};
    setStatus('off');
    setEnabled(false);
  }, []);

  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  useEffect(() => {
    if (!socket) return;

    const handleOffer = async ({ fromSocketId, offer }) => {
      const pc = createPeerConnection(fromSocketId);
      await pc.setRemoteDescription(new RTCSessionDescription(offer));
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      socket.emit('webrtc-answer', { targetSocketId: fromSocketId, answer });
    };

    const handleAnswer = async ({ fromSocketId, answer }) => {
      const pc = peersRef.current[fromSocketId];
      if (pc) await pc.setRemoteDescription(new RTCSessionDescription(answer));
    };

    const handleIce = ({ fromSocketId, candidate }) => {
      const pc = peersRef.current[fromSocketId];
      if (pc && candidate) pc.addIceCandidate(new RTCIceCandidate(candidate));
    };

    socket.on('webrtc-offer', handleOffer);
    socket.on('webrtc-answer', handleAnswer);
    socket.on('webrtc-ice-candidate', handleIce);

    return () => {
      socket.off('webrtc-offer', handleOffer);
      socket.off('webrtc-answer', handleAnswer);
      socket.off('webrtc-ice-candidate', handleIce);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket]);

  const createPeerConnection = (targetSocketId) => {
    if (peersRef.current[targetSocketId]) return peersRef.current[targetSocketId];

    const pc = new RTCPeerConnection(ICE_SERVERS);
    peersRef.current[targetSocketId] = pc;

    pc.onicecandidate = (e) => {
      if (e.candidate && socket) {
        socket.emit('webrtc-ice-candidate', { targetSocketId, candidate: e.candidate });
      }
    };

    pc.ontrack = (e) => {
      let audio = audioElementsRef.current[targetSocketId];
      if (!audio) {
        audio = new Audio();
        audio.autoplay = true;
        audioElementsRef.current[targetSocketId] = audio;
      }
      audio.srcObject = e.streams[0];
    };

    pc.onconnectionstatechange = () => {
      const allStates = Object.values(peersRef.current).map(p => p.connectionState);
      if (allStates.every(s => s === 'connected')) setStatus('connected');
      else if (allStates.some(s => s === 'failed')) setStatus('error');
      else setStatus('connecting');
    };

    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => pc.addTrack(track, localStreamRef.current));
    }

    return pc;
  };

  const enableVoice = async () => {
    try {
      setStatus('connecting');
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      localStreamRef.current = stream;
      setEnabled(true);

      // Connect to each other player
      const otherPlayers = players.filter(p => p.socketId !== socket.id);
      for (const p of otherPlayers) {
        const pc = createPeerConnection(p.socketId);
        stream.getTracks().forEach(track => pc.addTrack(track, stream));
        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);
        socket.emit('webrtc-offer', { targetSocketId: p.socketId, offer });
      }
    } catch (err) {
      console.error('Mic access denied:', err);
      setStatus('error');
    }
  };

  const toggleMute = () => {
    if (localStreamRef.current) {
      localStreamRef.current.getAudioTracks().forEach(t => { t.enabled = muted; });
      setMuted(!muted);
    }
  };

  const statusColors = { off: '#4b5563', connecting: '#f59e0b', connected: '#22c55e', error: '#ef4444' };

  return (
    <div style={{
      display: 'flex', gap: 4, alignItems: 'center', justifyContent: 'center'
    }}>
      {/* Status dot */}
      <div style={{
        width: 6, height: 6, borderRadius: '50%',
        background: statusColors[status]
      }} />

      {!enabled ? (
        <button onClick={enableVoice} style={{
          background: '#161b22', border: '1px solid #30363d', borderRadius: 4,
          color: '#e2e8f0', padding: '4px 10px', fontSize: 10, cursor: 'pointer',
          display: 'flex', alignItems: 'center', gap: 4
        }}>
          {'\uD83C\uDF99'} Voice
        </button>
      ) : (
        <>
          <button onClick={toggleMute} style={{
            background: muted ? '#7f1d1d' : '#161b22',
            border: `1px solid ${muted ? '#ef4444' : '#30363d'}`,
            borderRadius: 4, color: muted ? '#ef4444' : '#e2e8f0',
            padding: '4px 10px', fontSize: 10, cursor: 'pointer'
          }}>
            {muted ? '\uD83D\uDD07 Muted' : '\uD83C\uDF99 Live'}
          </button>
          <button onClick={cleanup} style={{
            background: '#161b22', border: '1px solid #30363d', borderRadius: 4,
            color: '#8b949e', padding: '4px 8px', fontSize: 10, cursor: 'pointer'
          }}>
            End
          </button>
        </>
      )}
    </div>
  );
}
